import { jsx, jsxs } from "react/jsx-runtime";
import { c } from "react-compiler-runtime";
import { memo } from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HeadingNode } from "@lexical/rich-text";
import { LinkNode } from "@lexical/link";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";
import { ListItemNode, ListNode } from "@lexical/list";
import { SHARED_EDITOR_CONFIG } from "../input/index.js";
import { useLogger } from "../services/hooks/index.js";
import { CodeBlockNode, FileNode, ImagesGalleryNode, MentionNode, QuoteNode, ReferenceNode, SingleImageNode, VideoNode } from "../input/WysiwygInput/nodes/index.js";
import { AdditionalFetchDataHelpersPlugin } from "../input/WysiwygInput/plugins/index.js";
import { WysiwygContext } from "../input/WysiwygInput/context/index.js";
import { UpdateStatePlugin } from "./plugins/index.js";
function _define_property(obj, key, value) {
    if (key in obj) Object.defineProperty(obj, key, {
        value: value,
        enumerable: true,
        configurable: true,
        writable: true
    });
    else obj[key] = value;
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = null != arguments[i] ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if ("function" == typeof Object.getOwnPropertySymbols) ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
            return Object.getOwnPropertyDescriptor(source, sym).enumerable;
        }));
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function WysiwygContent_ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) symbols = symbols.filter(function(sym) {
            return Object.getOwnPropertyDescriptor(object, sym).enumerable;
        });
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = null != source ? source : {};
    if (Object.getOwnPropertyDescriptors) Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    else WysiwygContent_ownKeys(Object(source)).forEach(function(key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
    return target;
}
const WysiwygContent = /*#__PURE__*/ memo((t0)=>{
    var _envVars_allowDirectVideoLinks;
    const $ = c(68);
    const { editorStateJSON, apiUrl, envVars: t1, config: t2, getAttachmentUrl } = t0;
    let t3;
    if ($[0] !== t1) {
        t3 = void 0 === t1 ? {
            allowDirectVideoLinks: false
        } : t1;
        $[0] = t1;
        $[1] = t3;
    } else t3 = $[1];
    const envVars = t3;
    let t4;
    if ($[2] !== t2) {
        t4 = void 0 === t2 ? {
            plugins: {
                selectAllPlugin: {
                    value: true,
                    withDivider: true
                },
                formatHeadingPlugin: {
                    heading: true,
                    fontColor: true,
                    backgroungColor: true,
                    withDivider: true
                },
                formatTextPlugin: {
                    bold: true,
                    italic: true,
                    underline: true,
                    strikethrough: true,
                    code: true,
                    superscript: true,
                    subscript: true,
                    withDivider: true
                },
                listAndFormatPlugin: {
                    align: true,
                    list: true,
                    indent: true,
                    withDivider: true
                },
                linkAndTablePlugin: {
                    link: true,
                    table: true,
                    reference: true,
                    codeBlock: true,
                    quote: true,
                    symbols: true,
                    mention: true,
                    withDivider: true
                },
                mediaPlugin: {
                    singeImage: true,
                    imagesGallery: true,
                    video: true,
                    file: true,
                    withDivider: true
                },
                undoRedoPlugin: true,
                trackMediaIdsAndMentionsPlugin: true
            },
            mode: "default"
        } : t2;
        $[2] = t2;
        $[3] = t4;
    } else t4 = $[3];
    const config = t4;
    const logger = useLogger();
    let t5;
    if ($[4] !== logger) {
        t5 = (editorError)=>{
            logger.error(editorError, {});
        };
        $[4] = logger;
        $[5] = t5;
    } else t5 = $[5];
    const handleEditorError = t5;
    const t6 = null != apiUrl ? apiUrl : "";
    const t7 = null != (_envVars_allowDirectVideoLinks = envVars.allowDirectVideoLinks) ? _envVars_allowDirectVideoLinks : false;
    let t8;
    if ($[6] !== t7) {
        t8 = {
            allowDirectVideoLinks: t7
        };
        $[6] = t7;
        $[7] = t8;
    } else t8 = $[7];
    let t9;
    if ($[8] !== t6 || $[9] !== t8) {
        t9 = {
            apiUrl: t6,
            envVars: t8
        };
        $[8] = t6;
        $[9] = t8;
        $[10] = t9;
    } else t9 = $[10];
    const wysiwygContext = t9;
    let t10;
    if ($[11] !== config.plugins.formatHeadingPlugin.heading) {
        t10 = config.plugins.formatHeadingPlugin.heading ? [
            HeadingNode
        ] : [];
        $[11] = config.plugins.formatHeadingPlugin.heading;
        $[12] = t10;
    } else t10 = $[12];
    let t11;
    if ($[13] !== config.plugins.linkAndTablePlugin.link) {
        t11 = config.plugins.linkAndTablePlugin.link ? [
            LinkNode
        ] : [];
        $[13] = config.plugins.linkAndTablePlugin.link;
        $[14] = t11;
    } else t11 = $[14];
    let t12;
    if ($[15] !== config.plugins.linkAndTablePlugin.table) {
        t12 = config.plugins.linkAndTablePlugin.table ? [
            TableNode,
            TableRowNode,
            TableCellNode
        ] : [];
        $[15] = config.plugins.linkAndTablePlugin.table;
        $[16] = t12;
    } else t12 = $[16];
    let t13;
    if ($[17] !== config.plugins.linkAndTablePlugin.mention) {
        t13 = config.plugins.linkAndTablePlugin.mention ? [
            MentionNode
        ] : [];
        $[17] = config.plugins.linkAndTablePlugin.mention;
        $[18] = t13;
    } else t13 = $[18];
    let t14;
    if ($[19] !== config.plugins.linkAndTablePlugin.reference) {
        t14 = config.plugins.linkAndTablePlugin.reference ? [
            ReferenceNode
        ] : [];
        $[19] = config.plugins.linkAndTablePlugin.reference;
        $[20] = t14;
    } else t14 = $[20];
    let t15;
    if ($[21] !== config.plugins.linkAndTablePlugin.codeBlock) {
        t15 = config.plugins.linkAndTablePlugin.codeBlock ? [
            CodeBlockNode
        ] : [];
        $[21] = config.plugins.linkAndTablePlugin.codeBlock;
        $[22] = t15;
    } else t15 = $[22];
    let t16;
    if ($[23] !== config.plugins.linkAndTablePlugin.quote) {
        t16 = config.plugins.linkAndTablePlugin.quote ? [
            QuoteNode
        ] : [];
        $[23] = config.plugins.linkAndTablePlugin.quote;
        $[24] = t16;
    } else t16 = $[24];
    let t17;
    if ($[25] !== config.plugins.listAndFormatPlugin.list) {
        t17 = config.plugins.listAndFormatPlugin.list ? [
            ListNode,
            ListItemNode
        ] : [];
        $[25] = config.plugins.listAndFormatPlugin.list;
        $[26] = t17;
    } else t17 = $[26];
    let t18;
    if ($[27] !== config.plugins.mediaPlugin.singeImage) {
        t18 = config.plugins.mediaPlugin.singeImage ? [
            SingleImageNode
        ] : [];
        $[27] = config.plugins.mediaPlugin.singeImage;
        $[28] = t18;
    } else t18 = $[28];
    let t19;
    if ($[29] !== config.plugins.mediaPlugin.imagesGallery) {
        t19 = config.plugins.mediaPlugin.imagesGallery ? [
            ImagesGalleryNode
        ] : [];
        $[29] = config.plugins.mediaPlugin.imagesGallery;
        $[30] = t19;
    } else t19 = $[30];
    let t20;
    if ($[31] !== config.plugins.mediaPlugin.video) {
        t20 = config.plugins.mediaPlugin.video ? [
            VideoNode
        ] : [];
        $[31] = config.plugins.mediaPlugin.video;
        $[32] = t20;
    } else t20 = $[32];
    let t21;
    if ($[33] !== config.plugins.mediaPlugin.file) {
        t21 = config.plugins.mediaPlugin.file ? [
            FileNode
        ] : [];
        $[33] = config.plugins.mediaPlugin.file;
        $[34] = t21;
    } else t21 = $[34];
    let t22;
    if ($[35] !== t10 || $[36] !== t11 || $[37] !== t12 || $[38] !== t13 || $[39] !== t14 || $[40] !== t15 || $[41] !== t16 || $[42] !== t17 || $[43] !== t18 || $[44] !== t19 || $[45] !== t20 || $[46] !== t21) {
        t22 = _object_spread_props(_object_spread({}, SHARED_EDITOR_CONFIG), {
            nodes: [
                ...t10,
                ...t11,
                ...t12,
                ...t13,
                ...t14,
                ...t15,
                ...t16,
                ...t17,
                ...t18,
                ...t19,
                ...t20,
                ...t21
            ]
        });
        $[35] = t10;
        $[36] = t11;
        $[37] = t12;
        $[38] = t13;
        $[39] = t14;
        $[40] = t15;
        $[41] = t16;
        $[42] = t17;
        $[43] = t18;
        $[44] = t19;
        $[45] = t20;
        $[46] = t21;
        $[47] = t22;
    } else t22 = $[47];
    const editorConfig = t22;
    let t23;
    if ($[48] !== editorConfig) {
        t23 = _object_spread_props(_object_spread({}, editorConfig), {
            namespace: "WysiwygContent"
        });
        $[48] = editorConfig;
        $[49] = t23;
    } else t23 = $[49];
    let t24;
    if ($[50] !== editorStateJSON) {
        t24 = (editor)=>{
            const parsedState = editor.parseEditorState(editorStateJSON);
            editor.setEditorState(parsedState);
        };
        $[50] = editorStateJSON;
        $[51] = t24;
    } else t24 = $[51];
    let t25;
    if ($[52] !== handleEditorError || $[53] !== t23 || $[54] !== t24) {
        t25 = _object_spread_props(_object_spread({}, t23), {
            editorState: t24,
            editable: false,
            onError: handleEditorError
        });
        $[52] = handleEditorError;
        $[53] = t23;
        $[54] = t24;
        $[55] = t25;
    } else t25 = $[55];
    let t26;
    if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
        t26 = /*#__PURE__*/ jsx(RichTextPlugin, {
            contentEditable: /*#__PURE__*/ jsx(ContentEditable, {
                readOnly: true,
                className: "read-only-content",
                "aria-readonly": "true",
                contentEditable: false
            }),
            placeholder: null,
            ErrorBoundary: LexicalErrorBoundary
        });
        $[56] = t26;
    } else t26 = $[56];
    let t27;
    if ($[57] !== editorStateJSON) {
        t27 = /*#__PURE__*/ jsx(UpdateStatePlugin, {
            state: editorStateJSON
        });
        $[57] = editorStateJSON;
        $[58] = t27;
    } else t27 = $[58];
    let t28;
    if ($[59] !== getAttachmentUrl || $[60] !== t27) {
        t28 = /*#__PURE__*/ jsxs(AdditionalFetchDataHelpersPlugin, {
            getAttachmentUrl: getAttachmentUrl,
            children: [
                t26,
                t27
            ]
        });
        $[59] = getAttachmentUrl;
        $[60] = t27;
        $[61] = t28;
    } else t28 = $[61];
    let t29;
    if ($[62] !== t25 || $[63] !== t28) {
        t29 = /*#__PURE__*/ jsx(LexicalComposer, {
            initialConfig: t25,
            children: t28
        });
        $[62] = t25;
        $[63] = t28;
        $[64] = t29;
    } else t29 = $[64];
    let t30;
    if ($[65] !== t29 || $[66] !== wysiwygContext) {
        t30 = /*#__PURE__*/ jsx(WysiwygContext.Provider, {
            value: wysiwygContext,
            children: t29
        });
        $[65] = t29;
        $[66] = wysiwygContext;
        $[67] = t30;
    } else t30 = $[67];
    return t30;
});
export { WysiwygContent };
